import { z } from "zod";
import { email } from "./schemas/email";
import { firstName } from "./schemas/first-name";
import { lastName } from "./schemas/last-name";
import { phone } from "./schemas/phone";
import { postalCode } from "./schemas/postal";

export const formVariantSchema = z.enum(["v1", "v2"]).default("v1");

export type FormVariant = z.infer<typeof formVariantSchema>;

export const careSeekerShallowLeadFormSchema = z.object({
  step1: z.object({
    formVariant: formVariantSchema,
    leadSource: z.enum(["RPN", "PPC"]).default("PPC"),
    role: z.literal("care-seeker"),
  }),
  step2: z.object({
    careFor: z.enum(["parent", "spouse", "me", "other"]),
  }),
  step3: z
    .object({
      everydayTasks: z.boolean().default(false),
      memoryCare: z.boolean().default(false),
      mobilityAssistance: z.boolean().default(false),
      other: z.boolean().default(false),
    })
    .refine((data) => Object.values(data).some((val) => val === true), {
      error: "Please select at least one care option",
    }),
  step4: z.object({
    // This is what the user types in
    location: postalCode,
    organizationNumber: z.number(),

    // This is the postal code provided by the LocationSelect component we send to our backend
    postalCode: z.string().optional(),
  }),
  step5: z.object({
    consentTerms: z.boolean().optional(),
    email,
    firstName,
    lastName,
    OptInEmail: z.boolean().default(true),
    OptInSms: z.boolean().default(false),
    phone,
  }),
});

export type CareSeekerShallowLeadFormData = z.input<
  typeof careSeekerShallowLeadFormSchema
>;

export type ShallowCareOption = keyof CareSeekerShallowLeadFormData["step3"];

// For multiple selections (one or more)
export type ShallowCareOptionSelection = [
  ShallowCareOption,
  ...ShallowCareOption[],
];

export const parseCareSeekerShallowFormData = (formData: FormData) => {
  return {
    step1: {
      formVariant: formData.get("step1.formVariant") || "v1",
      leadSource: formData.get("step1.leadSource"),
      role: formData.get("step1.role") || "care-seeker",
    },
    step2: {
      careFor: formData.get("step2.careFor"),
    },
    step3: {
      everydayTasks: formData.get("step3.everydayTasks") === "on",
      memoryCare: formData.get("step3.memoryCare") === "on",
      mobilityAssistance: formData.get("step3.mobilityAssistance") === "on",
      other: formData.get("step3.other") === "on",
    },
    step4: {
      // read both user entered location and final postalCode
      location: formData.get("step4.location"),
      organizationNumber: Number(formData.get("step4.organizationNumber")),
      postalCode: formData.get("step4.postalCode"), // final derived code
    },
    step5: {
      email: formData.get("step5.email"),
      firstName: formData.get("step5.firstName"),
      lastName: formData.get("step5.lastName"),

      // Opt in to email by submitting form
      OptInEmail: true,

      // Opt in to SMS by checking checkbox
      OptInSms: formData.get("step5.consentTerms") === "on",
      phone: formData.get("step5.phone"),
    },
  };
};
